import './messages-inbox.scss';

import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';

import { type ResultType } from '../../constants/global';
import { useGlobalContext } from '../../context/global';
import { type IAgentHierarchyNode, type IChatCounterpart, type IMessagesInboxData } from '../../interface/peer-chat';
import {
	applyInboxFilter,
	buildInboxRows,
	filterNeedsUnitState,
	groupBySection,
	type IInboxRow,
	type IInboxUnit,
	INBOX_FILTERS,
	type InboxFilter,
	inboxSummaryLine,
	NO_AGENT_NAME,
	rowAriaLabel,
	rowTime,
	SECTION_LABEL,
} from '../../lib/inbox-rows';
import { presenceLine } from '../../lib/peer-chat-copy';
import { getResultAnalyticsByScope, type IAnalyticsScopeParams } from '../../lib/result-analytics-api';
import { fetchPeerPresence, PEER_PRESENCE_MAX_IDS } from '../../lib/support-api';
import { supportReachNotice } from '../../lib/support-reach-copy';
import { type IChildAgentData } from '../pages/child-agent';
import { avatarTint, initials } from '../pages/child-agent/utils';
import { childEntityLabelSingular, hasRealIsoDate, puStatus } from '../pages/overview/quick-analytics/utils';
import { StatusBadge, type StatusTone } from '../utils/status-badge';
import { SearchSVG } from '../utils/svgs';
import { UnreadPill } from '../utils/unread-pill';

const PRESENCE_REFRESH_MS = 30_000;
const UNITS_REFRESH_MS = 60_000;
/** `ResultAnalyticsParams` is the one api.core DTO that caps at 1000 — a whole level in one page. */
const UNITS_PAGE_SIZE = 1000;

const chunk = <T,>(items: T[], size: number): T[][] => {
	const out: T[][] = [];
	for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
	return out;
};

const UNIT_TONE: Record<IInboxRow['unitState'], StatusTone> = {
	pending: 'warning',
	conflict: 'danger',
	uploaded: 'success',
	unknown: 'neutral',
};

const UNIT_LABEL: Record<IInboxRow['unitState'], string> = {
	pending: 'Pending',
	conflict: 'Conflict',
	uploaded: 'Uploaded',
	unknown: '',
};

/** The level one rung below the reader, and the numeric scope id of the reader's own entity. */
const childScopeParams = (
	resultType: string | undefined,
	ownEntityId: string | undefined,
	electionId: number
): IAnalyticsScopeParams | null => {
	const id = Number(ownEntityId);
	if (!id || !electionId) return null;
	switch ((resultType ?? '').toLowerCase()) {
		case 'ec8b':
			return { electionId, resultType: 'ec8a', wardId: id, pageSize: UNITS_PAGE_SIZE };
		case 'ec8c':
			return { electionId, resultType: 'ec8b', lgaId: id, pageSize: UNITS_PAGE_SIZE };
		case 'ec8d':
			return { electionId, resultType: 'ec8c', stateId: id, pageSize: UNITS_PAGE_SIZE };
		default:
			return null;
	}
};

const splitName = (name: string): [string, string] => {
	const parts = name.trim().split(/\s+/);
	return [parts[0] ?? '', parts.slice(1).join(' ')];
};

const Skeleton = () => (
	<ul
		className='msg-inbox__list msg-inbox__list--skeleton'
		aria-hidden='true'
	>
		{[0, 1, 2].map((i) => (
			<li
				key={i}
				className='msg-inbox__skeleton'
			/>
		))}
	</ul>
);

/**
 * A parent's Messages inbox — or a child's one-row version, their parent.
 *
 * <p>Rows are the parent's UNITS, joined with the agent who covers each (api.core's hierarchy,
 * which is also who the server will let the reader message) and with whatever thread exists.
 * Not the agents alone: a unit nobody covers is the gap a parent most needs to see, and not the
 * threads alone: the agent who has never been messaged is exactly the person to reach. Sort:
 * unread first, then units still pending, then recency.</p>
 */
const MessagesInboxPanel = () => {
	const { rsProps, supportSession, agentHierarchy, peerInbox, states } = useGlobalContext();
	const data = (rsProps?.data ?? null) as IMessagesInboxData | null;
	const me = supportSession?.me ?? 0;
	const electionOfficial = states?._election?.get_ElectionOfficial?.data?.find((i) => i.userId === me);
	const electionId = data?.electionId || electionOfficial?.electionId || 0;
	const ownResultType = electionOfficial?.assignment?.resultType?.toLowerCase();
	const isParent = !!ownResultType && ownResultType !== 'ec8a';
	const childLevelLabel = childEntityLabelSingular(
		({ ec8b: 'EC8A', ec8c: 'EC8B', ec8d: 'EC8C' } as Record<string, ResultType>)[ownResultType ?? '']
	);

	const agents: IAgentHierarchyNode[] = useMemo(
		() => (isParent ? (agentHierarchy?.children ?? []) : (agentHierarchy?.parents ?? []).slice(0, 1)),
		[isParent, agentHierarchy?.children, agentHierarchy?.parents]
	);

	const [filter, setFilter] = useState<InboxFilter>('all');
	const [search, setSearch] = useState('');

	// ── The parent's units, from the ledger the overview list already reads ────────────────
	const scope = useMemo(
		() => (isParent ? childScopeParams(ownResultType, electionOfficial?.assignment?.id, electionId) : null),
		[isParent, ownResultType, electionOfficial?.assignment?.id, electionId]
	);
	const [units, setUnits] = useState<IInboxUnit[] | null>(null);
	const [unitsState, setUnitsState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
	useEffect(() => {
		if (!isParent) return;
		if (!scope) {
			// A parent whose own entity id cannot be read: the ledger cannot be scoped. Say so
			// rather than listing agents as if every one of them were a pending unit.
			setUnitsState('error');
			return;
		}
		let cancelled = false;
		const load = () => {
			setUnitsState((s) => (s === 'idle' ? 'loading' : s));
			void getResultAnalyticsByScope(scope).then((outcome) => {
				if (cancelled) return;
				if (!outcome.reachable || outcome.httpStatus < 200 || outcome.httpStatus >= 300) {
					setUnitsState('error');
					return;
				}
				const rows = outcome.body?.data ?? [];
				setUnits(
					rows.map((row) => ({
						code: row.code,
						unitState: puStatus(row),
						uploadedAt: hasRealIsoDate(row.dateUploaded) ? row.dateUploaded : null,
						analytics: { isUploaded: row.isUploaded, hasConflict: row.hasConflict, dateUploaded: row.dateUploaded },
					}))
				);
				setUnitsState('ready');
			});
		};
		load();
		const timer = window.setInterval(load, UNITS_REFRESH_MS);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [isParent, scope]);

	// ── Presence, batched POSTs ─────────────────────────────────────────────────────────────
	const [presence, setPresence] = useState<Map<number, { lastSeenAt: string | null; inThreadKey: string | null }>>(new Map());
	const idsKey = agents
		.map((a) => a.userId)
		.sort((a, b) => a - b)
		.join(',');
	useEffect(() => {
		if (!electionId || !idsKey) return;
		const ids = idsKey.split(',').map(Number);
		let cancelled = false;
		const load = () => {
			void Promise.all(chunk(ids, PEER_PRESENCE_MAX_IDS).map((batch) => fetchPeerPresence(electionId, batch))).then((pages) => {
				if (cancelled) return;
				setPresence(new Map(pages.flat().map((r) => [r.agentUserId, { lastSeenAt: r.lastSeenAt, inThreadKey: r.inThreadKey }])));
			});
		};
		load();
		const timer = window.setInterval(load, PRESENCE_REFRESH_MS);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [electionId, idsKey]);

	// The panel asked for the list now; the poll's post-404 hold does not apply to user intent.
	const refreshRef = useRef(peerInbox?.refresh);
	refreshRef.current = peerInbox?.refresh;
	useEffect(() => {
		refreshRef.current?.();
	}, []);

	const rows = useMemo(
		() =>
			buildInboxRows({
				units: isParent && unitsState === 'ready' ? units : null,
				agents,
				summaryFor: (id) => peerInbox?.summaryFor(id),
				liveUnread: peerInbox?.unreadByCounterpart,
				me,
			}),
		[isParent, unitsState, units, agents, peerInbox, me]
	);
	// Until the ledger has answered, the two state filters have nothing to filter on.
	const unitStateKnown = !isParent || unitsState === 'ready';
	const effectiveFilter: InboxFilter = !unitStateKnown && filterNeedsUnitState(filter) ? 'all' : filter;
	const visible = useMemo(() => applyInboxFilter(rows, effectiveFilter, search), [rows, effectiveFilter, search]);
	const groups = useMemo(() => groupBySection(visible), [visible]);

	if (!rsProps) return null;

	const openChat = (row: IInboxRow) => {
		const agent = agents.find((a) => a.userId === row.agentId);
		const counterpart: IChatCounterpart = {
			coreUserId: row.agentId,
			name: row.name,
			entityCode: row.code,
			level: agent?.level,
			role: isParent ? 'child' : 'parent',
			phone: agent?.phoneNumber ?? undefined,
		};
		rsProps.addRightSectionHistory();
		rsProps.callSection({
			action: 'view',
			component: 'agent-chat',
			title: row.name,
			data: { counterpart, electionId, origin: 'inbox' },
		});
	};

	/** A unit nobody covers opens the coverage drawer's gap state, not a chat. */
	const openCoverage = (row: IInboxRow) => {
		const coverage: IChildAgentData = {
			entityCode: row.code,
			entityName: row.code,
			level: childLevelLabel,
			electionId,
			analyticsStatus: row.analytics,
			childScope: null,
			isDirectChild: true,
		};
		rsProps.addRightSectionHistory();
		rsProps.callSection({ action: 'view', component: 'child-agent', title: 'Agent Coverage', data: coverage });
	};

	const open = (row: IInboxRow) => (row.hasAgent ? openChat(row) : openCoverage(row));

	const hierarchyLoading = !agentHierarchy || agentHierarchy.status === 'idle' || agentHierarchy.status === 'loading';
	const unitsLoading = isParent && !!scope && (unitsState === 'idle' || unitsState === 'loading');
	const reachNotice = peerInbox ? supportReachNotice(peerInbox.reach, { kind: 'peer', name: 'your agents' }) : null;
	// gate → loading → error → data: the line never invents a count from missing data.
	const summary = !isParent
		? null
		: unitsLoading
			? 'Checking units…'
			: unitsState === 'error'
				? 'Unit status unavailable right now'
				: inboxSummaryLine(rows, 'units');
	const summaryIsStatus = isParent && unitsState !== 'ready';
	const ownCode = electionOfficial?.assignment?.code;

	return (
		<div className='msg-inbox'>
			{reachNotice ? (
				<div className='sc-banner sc-banner--unavailable'>
					{reachNotice.message}
					{reachNotice.canRetry ? (
						<button
							type='button'
							className='sc-banner__action'
							onClick={() => peerInbox?.refresh()}
						>
							Try again
						</button>
					) : null}
				</div>
			) : null}

			{summary ? (
				<p
					className={`msg-inbox__summary ${summaryIsStatus ? 'msg-inbox__summary--status' : ''}`.trim()}
					role={summaryIsStatus ? 'status' : undefined}
				>
					{summary}
				</p>
			) : null}

			{isParent && rows.length > 0 ? (
				<div className='msg-inbox__toolbar'>
					<label className='qa-search'>
						<span className='qa-search__icon'>
							<SearchSVG fill='currentColor' />
						</span>
						<input
							type='search'
							className='qa-search__input'
							placeholder='Search by code or name…'
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							aria-label='Search agents by code or name'
						/>
					</label>
					<div
						className='qa-segmented'
						role='tablist'
						aria-label='Filter conversations'
					>
						{INBOX_FILTERS.map((tab) => {
							const needsState = filterNeedsUnitState(tab.value) && !unitStateKnown;
							return (
								<button
									key={tab.value}
									type='button'
									role='tab'
									aria-selected={effectiveFilter === tab.value}
									aria-disabled={needsState || undefined}
									title={needsState ? 'Unit status is not available yet' : undefined}
									className={`qa-segmented__item ${effectiveFilter === tab.value ? 'qa-segmented__item--active' : ''} ${needsState ? 'qa-segmented__item--disabled' : ''}`.trim()}
									onClick={() => {
										if (!needsState) setFilter(tab.value);
									}}
								>
									{tab.label}
								</button>
							);
						})}
					</div>
				</div>
			) : null}

			{hierarchyLoading ? (
				<>
					<span className='visually-hidden'>Loading your agents…</span>
					<Skeleton />
				</>
			) : agentHierarchy?.status === 'forbidden' || agentHierarchy?.status === 'error' ? (
				<div className='msg-inbox__empty'>
					<h3>Can&apos;t load your agents right now</h3>
					<p>Your conversations are still here. Try again in a moment.</p>
					<button
						type='button'
						className='sc-banner__action'
						onClick={() => agentHierarchy?.refresh()}
					>
						Try again
					</button>
				</div>
			) : rows.length === 0 ? (
				<div className='msg-inbox__empty'>
					{isParent ? (
						<>
							<h3>No agents assigned under {ownCode ?? 'your unit'} yet.</h3>
							<p>Coverage gaps are visible to the coordination team.</p>
						</>
					) : (
						<>
							<h3>No ward agent assigned yet.</h3>
							<p>The support desk can still help.</p>
							<button
								type='button'
								className='sc-banner__action'
								onClick={() => rsProps.callSection({ action: 'view', component: 'support-chat', title: 'Support desk' })}
							>
								Chat with support
							</button>
						</>
					)}
				</div>
			) : visible.length === 0 ? (
				<div className='msg-inbox__empty'>
					<h3>No agents match</h3>
					<p>Clear the filter or search to see everyone.</p>
				</div>
			) : (
				groups.map((group) => (
					<section
						key={group.section}
						className='msg-inbox__section'
						aria-labelledby={`msg-inbox-${group.section}`}
					>
						<h2
							id={`msg-inbox-${group.section}`}
							className='msg-inbox__eyebrow'
						>
							{SECTION_LABEL[group.section]} · {group.rows.length}
						</h2>
						<ul className='msg-inbox__list'>
							{group.rows.map((row) => {
								const time = rowTime(row.lastMessageAt);
								const [first, last] = splitName(row.name);
								const reading = row.hasAgent ? presence.get(row.agentId) : undefined;
								const presenceText = reading
									? presenceLine(
											{ lastSeenAt: reading.lastSeenAt, inThisThread: !!row.summary && reading.inThreadKey === row.summary.key },
											false
										)
									: null;
								const onKey = (e: KeyboardEvent) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										open(row);
									}
								};
								return (
									<li key={row.hasAgent ? `a${row.agentId}` : `u${row.code}`}>
										<div
											className={`msg-inbox__row ${row.unread > 0 ? 'msg-inbox__row--unread' : ''} ${row.hasAgent ? '' : 'msg-inbox__row--gap'}`.trim()}
											role='button'
											tabIndex={0}
											aria-label={rowAriaLabel(row, time)}
											onClick={() => open(row)}
											onKeyDown={onKey}
										>
											<span
												className={`msg-inbox__avatar ${row.hasAgent ? '' : 'msg-inbox__avatar--gap'}`.trim()}
												style={row.hasAgent ? { background: avatarTint(row.code || row.name) } : undefined}
												aria-hidden='true'
											>
												{row.hasAgent ? initials(first, last, row.name) : '—'}
											</span>
											<span className='msg-inbox__body'>
												<span className='msg-inbox__line'>
													<span className={`msg-inbox__name ${row.hasAgent ? '' : 'msg-inbox__name--gap'}`.trim()}>
														{row.hasAgent ? row.name : NO_AGENT_NAME}
													</span>
													{row.hasAgent && !row.isActive ? (
														<StatusBadge
															label='Inactive'
															tone='neutral'
															variant='text'
															size='sm'
														/>
													) : null}
													<span className='msg-inbox__time'>{time}</span>
												</span>
												<span className='msg-inbox__line'>
													<span className='msg-inbox__code'>{row.code}</span>
													{row.unitState !== 'unknown' ? (
														<StatusBadge
															label={UNIT_LABEL[row.unitState]}
															tone={UNIT_TONE[row.unitState]}
															size='sm'
														/>
													) : null}
												</span>
												<span className='msg-inbox__line'>
													<span className={`msg-inbox__preview ${row.preview ? '' : 'msg-inbox__preview--muted'}`}>
														{row.hasAgent ? row.preview || presenceText || 'No messages yet' : 'Open coverage'}
													</span>
													<UnreadPill count={row.unread} />
												</span>
											</span>
										</div>
									</li>
								);
							})}
						</ul>
					</section>
				))
			)}

			{isParent && peerInbox && peerInbox.totalCount > peerInbox.threads.length && peerInbox.threads.length > 0 ? (
				<p className='msg-inbox__footer'>
					Showing {peerInbox.threads.length} of {peerInbox.totalCount} conversations. Open the others from Result Analytics.
				</p>
			) : null}
		</div>
	);
};

export default MessagesInboxPanel;
